import { useContext } from 'react';
import { AppContext } from '@/context/AppContext';
import { getRankColor } from '@/utils/rankUtils';
import { formatDomain, truncate } from '@/utils/formatUtils';
import { buildCompetitorData, getSummaryStats } from '@/utils/pdfUtils';

const STAT_CARDS = [
  { key: 'top3', label: 'TOP 3', color: '#00C48C' },
  { key: 'page1', label: 'PAGE 1 (4-10)', color: '#60A5FA' },
  { key: 'page2', label: 'PAGE 2+', color: '#F59E0B' },
  { key: 'notRanked', label: 'NOT RANKED', color: '#EF4444' },
];

export default function ReportTemplate() {
  const { activeClient, rankings, activeClientId } = useContext(AppContext);
  const clientRankings = rankings[activeClientId] || [];
  const stats = getSummaryStats(clientRankings);
  const competitorData = buildCompetitorData(clientRankings);
  const maxCount = competitorData.reduce((max, item) => Math.max(max, item.count), 0) || 1;

  return (
    <div
      id="pdf-report"
      style={{
        visibility: 'hidden',
        position: 'fixed',
        pointerEvents: 'none',
        top: 0,
        left: 0,
        width: '800px',
        fontFamily: 'Syne, sans-serif',
      }}
    >
      {/* Page 1: Cover */}
      <div 
        className="pdf-page" 
        style={{ 
          width: '800px', 
          height: '1130px', 
          background: '#0A0F1E', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          color: '#E2E8F0', 
          padding: '60px',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ 
          width: '80px', 
          height: '80px', 
          background: 'linear-gradient(135deg, #00C48C, #0070F3)', 
          borderRadius: '16px', 
          marginBottom: '30px' 
        }} />
        <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>
          SEO RANKINGS REPORT
        </h1>
        {activeClient?.name && (
          <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#00C48C', marginBottom: '10px', textAlign: 'center' }}>
            {activeClient.name}
          </h2>
        )}
        {activeClient?.domain && (
          <p style={{ fontSize: '16px', color: '#94A3B8', marginBottom: '40px' }}>
            {formatDomain(activeClient.domain)}
          </p>
        )}
        <p style={{ fontSize: '14px', color: '#4A5568' }}>
          Report Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
        <div style={{ 
          position: 'absolute', 
          bottom: '0', 
          left: '0', 
          right: '0', 
          height: '8px', 
          background: 'linear-gradient(90deg, #00C48C, #0070F3)' 
        }} />
      </div>

      {/* Page 2: Summary */}
      <div 
        className="pdf-page" 
        style={{ 
          width: '800px', 
          minHeight: '1130px', 
          background: '#0A0F1E', 
          color: '#E2E8F0', 
          padding: '60px',
          boxSizing: 'border-box'
        }}
      >
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '40px', color: '#E2E8F0' }}>
          Summary Overview
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '40px' }}>
          {STAT_CARDS.map((card) => (
            <div
              key={card.key}
              style={{
                backgroundColor: `${card.color}10`,
                border: `1px solid ${card.color}30`,
                borderRadius: '12px',
                padding: '30px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '48px', fontWeight: '900', color: card.color, fontFamily: 'JetBrains Mono, monospace' }}>
                {stats[card.key]}
              </div>
              <div style={{ fontSize: '11px', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '10px', fontFamily: 'JetBrains Mono, monospace' }}>
                {card.label}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '40px', padding: '20px', background: '#131926', borderRadius: '12px', border: '1px solid #1E2D45' }}>
          <div style={{ fontSize: '12px', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px', fontFamily: 'JetBrains Mono, monospace' }}>
            Total Keywords Tracked
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#E2E8F0' }}>
            {clientRankings.length}
          </div>
        </div>
      </div>

      {/* Page 3: Rankings Table */}
      <div 
        className="pdf-page" 
        style={{ 
          width: '800px', 
          minHeight: '1130px', 
          background: '#0A0F1E', 
          color: '#E2E8F0', 
          padding: '60px',
          boxSizing: 'border-box'
        }}
      >
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', color: '#E2E8F0' }}>
          Keyword Rankings
        </h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ fontSize: '10px', textTransform: 'uppercase', color: '#4A5568', borderBottom: '1px solid #1E2D45', fontFamily: 'JetBrains Mono, monospace' }}>
              <th style={{ padding: '12px 8px', textAlign: 'left' }}>Keyword</th>
              <th style={{ padding: '12px 8px', textAlign: 'left' }}>Location</th>
              <th style={{ padding: '12px 8px', textAlign: 'center' }}>Rank</th>
              <th style={{ padding: '12px 8px', textAlign: 'left' }}>Snippet</th>
            </tr>
          </thead>
          <tbody>
            {clientRankings.sort((a, b) => (a.rank ?? 100) - (b.rank ?? 100)).slice(0, 15).map((ranking, index) => (
              <tr key={index} style={{ fontSize: '11px', borderBottom: '1px solid #1E2D45' }}>
                <td style={{ padding: '10px 8px', fontWeight: 'bold' }}>{truncate(ranking.keyword, 25)}</td>
                <td style={{ padding: '10px 8px', color: '#94A3B8' }}>{truncate(ranking.location, 20)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    color: getRankColor(ranking.rank),
                    backgroundColor: `${getRankColor(ranking.rank)}15`,
                    border: `1px solid ${getRankColor(ranking.rank)}30`,
                    fontFamily: 'JetBrains Mono, monospace',
                  }}>
                    {ranking.rank !== null && ranking.rank !== undefined ? ranking.rank : '—'}
                  </span>
                </td>
                <td style={{ padding: '10px 8px', fontStyle: 'italic', color: '#94A3B8', fontSize: '10px' }}>
                  {truncate(ranking.snippet, 35)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Page 4: Competitors */}
      {competitorData.length > 0 && (
        <div 
          className="pdf-page" 
          style={{ 
            width: '800px', 
            height: '1130px', 
            background: '#0A0F1E', 
            color: '#E2E8F0', 
            padding: '60px',
            boxSizing: 'border-box'
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '40px', color: '#E2E8F0' }}>
            Competitor Landscape
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {competitorData.map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '140px', fontSize: '12px', color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace', textAlign: 'right' }}>
                  {truncate(item.domain, 18)}
                </div>
                <div style={{ flex: 1, height: '24px', background: '#131926', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${(item.count / maxCount) * 100}%`,
                    height: '100%',
                    background: index === 0 ? '#0070F3' : '#1E2D45',
                    borderRadius: '4px',
                  }} />
                </div>
                <div style={{ width: '30px', fontSize: '12px', fontWeight: 'bold', color: '#E2E8F0', fontFamily: 'JetBrains Mono, monospace' }}>
                  {item.count}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '40px', padding: '16px', background: '#131926', borderRadius: '8px', border: '1px solid #1E2D45' }}>
            <p style={{ fontSize: '11px', color: '#94A3B8', margin: 0 }}>
              This chart shows how frequently each competitor appears in the top search results for your tracked keywords.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
